import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Session store. The access token is used as a Bearer credential; the refresh
 * token is used transparently by the API client to rotate the pair on 401.
 *
 * Note on storage: tokens are persisted to localStorage so sessions survive a
 * reload. This trades some XSS exposure for UX; it is mitigated server-side by
 * short access-token TTLs and refresh-token rotation with reuse detection. For
 * the strongest posture, serve the SPA and API same-origin and move the refresh
 * token to an httpOnly cookie (see README).
 *
 * Impersonation: when a super admin "logs in as" a user, their own session is
 * stashed under `impersonation.admin` and the active session becomes the
 * target's. "Back to Super Admin" restores the stash verbatim.
 *
 * The stashed admin tokens live in memory only and are never written to
 * localStorage: a super admin token is the most valuable thing a script on
 * this origin could steal. After a reload mid-session the stash is gone, so
 * "Back to Super Admin" ends the session and sends you to the admin sign-in.
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      // Active tenant context. Resolved after login by the useTenant bootstrap
      // and used to scope every workspace-scoped API call.
      orgId: null,
      workspaceId: null,
      // Present only while a super admin is acting as another user.
      impersonation: null,

      isAuthenticated: () => Boolean(get().accessToken),

      // A new session belongs to a (possibly) different user — drop any stale
      // tenant selection so the bootstrap re-resolves it.
      setSession: ({ user, accessToken, refreshToken }) =>
        set({ user, accessToken, refreshToken, orgId: null, workspaceId: null }),

      setUser: (user) => set({ user }),

      setTenant: ({ orgId, workspaceId }) => set({ orgId, workspaceId }),

      /** Stash the current (admin) session and switch to the target user's. */
      startImpersonation: (targetSession, adminInfo) => {
        const { user, accessToken, refreshToken, orgId, workspaceId } = get();
        set({
          impersonation: {
            admin: { user, accessToken, refreshToken, orgId, workspaceId },
            adminInfo,
          },
          user: targetSession.user,
          accessToken: targetSession.accessToken,
          refreshToken: targetSession.refreshToken,
          orgId: null,
          workspaceId: null,
        });
      },

      /**
       * Restore the stashed admin session. Returns the impersonated refresh
       * token so the caller can revoke it server-side.
       */
      endImpersonation: () => {
        const { impersonation, refreshToken: impersonatedRefreshToken } = get();
        if (!impersonation) return null;
        const { admin } = impersonation;
        if (!admin?.refreshToken) {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            orgId: null,
            workspaceId: null,
            impersonation: null,
          });
          return impersonatedRefreshToken;
        }
        set({
          user: admin.user,
          accessToken: admin.accessToken,
          refreshToken: admin.refreshToken,
          orgId: admin.orgId,
          workspaceId: admin.workspaceId,
          impersonation: null,
        });
        return impersonatedRefreshToken;
      },

      clear: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          orgId: null,
          workspaceId: null,
          impersonation: null,
        }),
    }),
    {
      name: 'mdu-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        orgId: state.orgId,
        workspaceId: state.workspaceId,
        // Keep who and until when, never the admin's tokens.
        impersonation: state.impersonation
          ? {
              adminInfo: state.impersonation.adminInfo,
              admin: { user: state.impersonation.admin?.user ?? null },
            }
          : null,
      }),
    },
  ),
);
